<script setup>
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {accessControlApi, articleListsApi, ga_Api} from "@/api/forwriting_index";
import {marked} from "marked";
import LoadingIcon from "@/components/LoadingIcon.vue";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const route = useRoute()
const router = useRouter();
const { t, locale } = useI18n();
const article_list_id = route.params.article_list_id

// Dashboard data
const stats = reactive({
  article_stats: {
    total: 0,
    generate_status: {"0": 0, "1": 0, "2": 0, "3": 0},
    cms_post_status: {"0": 0, "1": 0, "2": 0, "3": 0},
  },
  crawl_sites: {
    total: 0,
    active: 0,
    items: [],
  },
  recent_crawl_articles: [],
  ga_analysis: {
    ga_analysis_result: "",
    ga_match_prompt: "",
    ga_analyzed_at: "",
  }
})

// ==== GA Media Analysis (moved from fw23_setting.vue) ====
const ga_analysis_loading = ref(false)
const ga_analysis_msg = ref("")
const ga_analysis_success = ref(null)
const analysisLanguageTouched = ref(false)

const normalizeAnalysisLanguage = (currentLocale) => {
  if (["ja", "tw", "en"].includes(currentLocale)) {
    return currentLocale
  }
  return "ja"
}

const analysisLanguage = ref(normalizeAnalysisLanguage(locale.value))

watch(locale, (newLocale) => {
  if (!analysisLanguageTouched.value) {
    analysisLanguage.value = normalizeAnalysisLanguage(newLocale)
  }
})

const getLocalizedMessage = (message, fallbackKey) => {
  if (!message) return t(fallbackKey)
  if (typeof message === "string" && message.includes("|")) {
    const [jaMsg, twMsg] = message.split("|")
    return locale.value === "tw" ? twMsg.trim() : jaMsg.trim()
  }
  return message
}

const formatLocalizedDateTime = (value) => {
  if (!value) return ""
  const date = new Date(value)
  const localeCodeMap = {
    ja: "ja-JP",
    tw: "zh-TW",
    en: "en-US",
  }
  const localeCode = localeCodeMap[locale.value] || "ja-JP"
  return date.toLocaleString(localeCode)
}

const handle_ga_analyze = async () => {
  ga_analysis_loading.value = true
  ga_analysis_msg.value = ""
  ga_analysis_success.value = null
  isLoading.show = true

  try {
    const resp = await ga_Api.analyze_media(article_list_id, analysisLanguage.value)
    if (resp.status === 200 && resp.data) {
      stats.ga_analysis.ga_analysis_result = resp.data.ga_analysis_result || ""
      stats.ga_analysis.ga_match_prompt = resp.data.ga_match_prompt || ""
      stats.ga_analysis.ga_analyzed_at = resp.data.ga_analyzed_at || ""
      ga_analysis_msg.value = t("dashboard.ga_analysis_success")
      ga_analysis_success.value = true
    }
  } catch (e) {
    console.error("GA analysis error:", e)
    let errorMessage = t("dashboard.ga_analysis_failed")
    if (e.response && e.response.data && e.response.data.detail) {
      errorMessage = getLocalizedMessage(e.response.data.detail, "dashboard.ga_analysis_failed")
    }
    ga_analysis_msg.value = errorMessage
    ga_analysis_success.value = false
  } finally {
    ga_analysis_loading.value = false
    isLoading.show = false
  }
}

const reload_data = async () => {
  try {
    const resp = await articleListsApi.get_dashboard_stats(article_list_id)
    if (resp.data) {
      Object.assign(stats.article_stats, resp.data.article_stats)
      Object.assign(stats.crawl_sites, resp.data.crawl_sites)
      stats.recent_crawl_articles = resp.data.recent_crawl_articles || []
      Object.assign(stats.ga_analysis, resp.data.ga_analysis)
    }
  } catch (e) {
    console.error("Dashboard data load error:", e)
  }
}

onMounted(async () => {
  if (!await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  isLoading.show = true
  await reload_data()
  isLoading.show = false

  // check the point_limit
  point_limit.is_over = await ref_CheckPointLimit.value.check()
})

const isLoading = reactive({show: false})
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({is_over: false})

// Markdown rendering
marked.setOptions({breaks: true, gfm: true})
const analysisResultHtml = computed(() => {
  if (!stats.ga_analysis.ga_analysis_result) return ""
  return marked.parse(stats.ga_analysis.ga_analysis_result)
})
const matchPromptHtml = computed(() => {
  if (!stats.ga_analysis.ga_match_prompt) return ""
  return marked.parse(stats.ga_analysis.ga_match_prompt)
})
</script>

<template>
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <div>

    <!-- Section 1: Article Stats -->
    <div class="row" style="margin-top: 10px">
      <h5>{{ $t('dashboard.article_stats_title') }}</h5>
      <hr>
    </div>

    <div class="row" style="margin-top: 10px">
      <!-- Total articles -->
      <div class="col-md-2">
        <div class="card text-center">
          <div class="card-body py-3">
            <h2 class="mb-0">{{ stats.article_stats.total }}</h2>
            <small class="text-muted">{{ $t('dashboard.total_articles') }}</small>
          </div>
        </div>
      </div>

      <!-- Generate status -->
      <div class="col-md-5">
        <div class="card">
          <div class="card-body py-3">
            <h6 class="card-title mb-2">{{ $t('dashboard.generate_status') }}</h6>
            <div class="d-flex gap-2 flex-wrap">
              <span class="badge bg-secondary">{{ $t('dashboard.status_not_started') }}: {{ stats.article_stats.generate_status['0'] }}</span>
              <span class="badge bg-primary">{{ $t('dashboard.status_in_progress_gen') }}: {{ stats.article_stats.generate_status['1'] }}</span>
              <span class="badge bg-success">{{ $t('dashboard.status_completed') }}: {{ stats.article_stats.generate_status['2'] }}</span>
              <span class="badge bg-danger">{{ $t('dashboard.status_error') }}: {{ stats.article_stats.generate_status['3'] }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- CMS post status -->
      <div class="col-md-5">
        <div class="card">
          <div class="card-body py-3">
            <h6 class="card-title mb-2">{{ $t('dashboard.cms_post_status') }}</h6>
            <div class="d-flex gap-2 flex-wrap">
              <span class="badge bg-secondary">{{ $t('dashboard.status_not_started') }}: {{ stats.article_stats.cms_post_status['0'] }}</span>
              <span class="badge bg-primary">{{ $t('dashboard.status_in_progress_post') }}: {{ stats.article_stats.cms_post_status['1'] }}</span>
              <span class="badge bg-success">{{ $t('dashboard.status_completed') }}: {{ stats.article_stats.cms_post_status['2'] }}</span>
              <span class="badge bg-danger">{{ $t('dashboard.status_error') }}: {{ stats.article_stats.cms_post_status['3'] }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Media Analysis (moved from fw23_setting.vue) -->
    <div class="row" style="margin-top: 40px">
      <h5>{{ $t('dashboard.media_analysis_title') }}</h5>
      <hr>
    </div>

    <div class="row" style="margin-top: 10px; margin-bottom: 20px">
      <div style="margin-left: 10px; max-width: 800px">
        <small class="text-muted">{{ $t('setting.ga_analysis_description') }}</small>
      </div>
    </div>

    <div class="row" style="margin-top: 10px">
      <div>
        <label class="form-label d-block" style="margin-left: 10px">
          {{ $t('dashboard.analysis_language_label') }}
        </label>
        <select
          v-model="analysisLanguage"
          class="form-select"
          style="width: 220px; margin-left: 10px; margin-bottom: 12px"
          @change="analysisLanguageTouched = true"
          :disabled="ga_analysis_loading"
        >
          <option value="ja">{{ $t('dashboard.analysis_language_ja') }}</option>
          <option value="tw">{{ $t('dashboard.analysis_language_tw') }}</option>
          <option value="en">{{ $t('dashboard.analysis_language_en') }}</option>
        </select>
        <button class="btn btn-primary" style="width: 300px; margin-left: 10px"
                @click="handle_ga_analyze"
                :disabled="ga_analysis_loading">
          {{ ga_analysis_loading ? $t('setting.ga_analyzing') : $t('setting.ga_analyze_button') }}
        </button>
        <span v-if="ga_analysis_msg" style="margin-left: 15px"
              :class="ga_analysis_success ? 'text-success' : 'text-danger'">
          {{ ga_analysis_msg }}
        </span>
      </div>
    </div>

    <div v-if="stats.ga_analysis.ga_analyzed_at" class="row" style="margin-top: 10px; margin-left: 10px">
      <small class="text-muted">
        {{ $t('setting.ga_last_analyzed') }}: {{ formatLocalizedDateTime(stats.ga_analysis.ga_analyzed_at) }}
      </small>
    </div>

    <!-- Analysis Result -->
    <div v-if="stats.ga_analysis.ga_analysis_result" class="row" style="margin-top: 20px">
      <div style="margin-left: 10px; max-width: 800px">
        <label class="form-label"><strong>{{ $t('setting.ga_analysis_result_label') }}</strong></label>
        <div class="card">
          <div class="card-body markdown-body" style="font-size: 14px; max-height: 400px; overflow-y: auto;"
               v-html="analysisResultHtml"></div>
        </div>
      </div>
    </div>

    <!-- Match Prompt -->
    <div v-if="stats.ga_analysis.ga_match_prompt" class="row" style="margin-top: 20px">
      <div style="margin-left: 10px; max-width: 800px">
        <label class="form-label"><strong>{{ $t('setting.ga_match_prompt_label') }}</strong></label>
        <div class="card">
          <div class="card-body markdown-body" style="font-size: 14px; background-color: #f8f9fa;"
               v-html="matchPromptHtml"></div>
        </div>
        <small class="text-muted" style="margin-top: 5px; display: block">
          {{ $t('setting.ga_match_prompt_hint') }}
        </small>
      </div>
    </div>

    <!-- Section 3: Recent Crawl Articles -->
    <div class="row" style="margin-top: 40px">
      <h5>{{ $t('dashboard.recent_articles_title') }}</h5>
      <hr>
    </div>

    <div v-if="stats.recent_crawl_articles.length === 0" class="row" style="margin-left: 10px">
      <small class="text-muted">{{ $t('dashboard.recent_articles_empty') }}</small>
    </div>

    <div v-else class="row" style="margin-top: 10px">
      <table class="table table-sm table-hover" style="max-width: 900px; margin-left: 10px">
        <thead>
          <tr>
            <th>{{ $t('dashboard.article_title') }}</th>
            <th>{{ $t('dashboard.source_site') }}</th>
            <th>{{ $t('dashboard.match_score') }}</th>
            <th>{{ $t('dashboard.crawled_at') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in stats.recent_crawl_articles" :key="article.id">
            <td>
              <a :href="article.url" target="_blank" style="font-size: 14px">{{ article.title }}</a>
            </td>
            <td><small>{{ article.site_name }}</small></td>
            <td>
              <span class="badge"
                    :class="article.match_score >= 70 ? 'bg-success' : article.match_score >= 40 ? 'bg-warning text-dark' : 'bg-secondary'">
                {{ article.match_score != null ? article.match_score : '-' }}
              </span>
            </td>
            <td><small>{{ formatLocalizedDateTime(article.created_at) }}</small></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Section 4: Crawl Sites Status -->
    <div class="row" style="margin-top: 40px">
      <h5>
        {{ $t('dashboard.crawl_sites_title') }}
        <small class="text-muted" style="font-size: 14px; margin-left: 10px">
          ({{ $t('dashboard.active_sites') }}: {{ stats.crawl_sites.active }} / {{ $t('dashboard.total_sites') }}: {{ stats.crawl_sites.total }})
        </small>
      </h5>
      <hr>
    </div>

    <div v-if="stats.crawl_sites.items.length === 0" class="row" style="margin-left: 10px">
      <small class="text-muted">{{ $t('dashboard.crawl_sites_empty') }}</small>
    </div>

    <div v-else class="row" style="margin-top: 10px; margin-bottom: 40px">
      <table class="table table-sm table-hover" style="max-width: 900px; margin-left: 10px">
        <thead>
          <tr>
            <th>{{ $t('dashboard.site_name') }}</th>
            <th>{{ $t('dashboard.last_crawled') }}</th>
            <th>{{ $t('dashboard.status_label') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="site in stats.crawl_sites.items" :key="site.id">
            <td>{{ site.site_name }}</td>
            <td>
              <small v-if="site.last_crawled_at">{{ formatLocalizedDateTime(site.last_crawled_at) }}</small>
              <small v-else class="text-muted">{{ $t('dashboard.not_crawled_yet') }}</small>
            </td>
            <td>
              <span class="badge" :class="site.is_active ? 'bg-success' : 'bg-secondary'">
                {{ site.is_active ? $t('dashboard.active') : $t('dashboard.inactive') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<style scoped>
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3) {
  font-size: 1.1em;
  font-weight: 600;
  margin-top: 16px;
  margin-bottom: 8px;
  border-bottom: none;
}
.markdown-body :deep(h4),
.markdown-body :deep(h5) {
  font-size: 1em;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 6px;
}
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 20px;
  margin-bottom: 8px;
}
.markdown-body :deep(li) {
  margin-bottom: 4px;
}
.markdown-body :deep(p) {
  margin-bottom: 8px;
}
.markdown-body :deep(strong) {
  font-weight: 600;
}
.markdown-body :deep(code) {
  background-color: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.9em;
}
</style>
